import {
  findEvaluationDetailsByPaperIdAndDate,
  findEvaluationByIdForNationalAverage,
  findSubsectionDetailsById,
  findCandidateResultByID,
  isAddNationalAverage,
  findPapersByTestTrackId,
  findCandidateReportsForNationalAverage,
} from 'gql/query'
import { changeFormatCandidateWise } from './nationalAverageForSectionWise'
import { ISubsection } from 'type'
import { changeformat } from './sectionBasedFormat'

/** Match backend automation lookback when calculating national average (see nationalAverageHelpers.calculateNationalAverage). */
const DEFAULT_LOOKBACK_DAYS = 365

//---------------------------------------------------------------------------------------------->
const findTotalSectionalAverage = async (data: any) => {
  try {
    if (data.length > 0) {
      let total_marks = 0
      let total_marks_obtained = 0
      data.forEach((item: any) => {
        total_marks += item.totalMarks
        total_marks_obtained += item?.totalMarksObtained
        const sectionTotal = parseInt(item?.totalMarks) || 0
        item.sectional_average = sectionTotal > 0 ? (parseInt(item?.totalMarksObtained) / sectionTotal) * 100 : 0
      })
      if (total_marks <= 0) {
        return { status: false, message: "Can't calculate national average - no marks found on related papers" }
      }
      const national_average_of_paper = (total_marks_obtained / total_marks) * 100
      return { status: true, data, national_average_of_paper }
    } else {
      return { status: false, message: "Can't add now, there is no related papers " }
    }
  } catch (err) {
    return { status: false, message: 'unexpected issues found.........' }
  }
}
//---------------------------------------------------------------------------------------------------------------------
export const isCalculateNationalAverage = async (testTrackId: string, productId?: string) => {
  try {
    const currentDate: number = Date.now()
    const formatedCurrentDate: Date = new Date(currentDate)
    //finding national average with productId and test track
    const addNationalAverage: any = await isAddNationalAverage(testTrackId, productId)
    //if national average is already there
    if (addNationalAverage?.national_average?.length > 0) {
      const createdDate: string = addNationalAverage?.national_average[0]?.created_at
      const nationalAverageId: string = addNationalAverage?.national_average[0]?.id
      const newCreateDate: Date = new Date(createdDate)
      const timeDifference: number = formatedCurrentDate.getTime() - newCreateDate.getTime()
      const daysDifference: number = timeDifference / (1000 * 60 * 60 * 24)
      //Is national average created before 30 days
      //user have to update
      if (daysDifference > 30) {
        // Keep existing row so callers can fall back if recalculation fails
        return { status: false, canUpdate: true, id: nationalAverageId, addNationalAverage }
      } else {
        //user can't update
        return { status: true, addNationalAverage }
      }
    } else {
      //its not present then user have to create new one
      return { status: false }
    }
  } catch (err) {
    console.log(err, ':error ')
    //returning nothing here made callers treat a failed lookup as "not present"
    return { status: false, lookupFailed: true }
  }
}

type FindNationalAverageOptions = {
  lookbackDays?: number
  /** When generating results, always include this evaluation even if it falls outside the lookback window. */
  ensureEvaluationId?: string
}

export const findNationalAverage = async (testTrackId: string, productId?: string, options?: FindNationalAverageOptions) => {
  try {
    const lookbackDays = options?.lookbackDays ?? DEFAULT_LOOKBACK_DAYS
    const questionsWithSameProductIdAndTestTrackId: any = await findPapersByTestTrackId(testTrackId, productId)
    const relatedPapers = questionsWithSameProductIdAndTestTrackId?.papers
    //the papers lookup returns nothing when it fails, which is not the same as
    //the test track genuinely having no papers
    if (!relatedPapers) {
      return { status: false, message: 'Could not look up the papers for this test track. Please try again.' }
    }

    // Match backend: only papers with a usable subsection/question structure
    const validPapers = relatedPapers.filter(
      (paper: any) => Array.isArray(paper?.subSectionQuestions) && paper.subSectionQuestions.length > 0
    )

    const currentDate = Date.now()
    const formatedCurrentDate = new Date(currentDate)
    formatedCurrentDate.setDate(formatedCurrentDate.getDate() - lookbackDays)
    const lookbackDate = formatedCurrentDate.toISOString()
    const responseEvaluationDetailsForPaper: any[] = []

    if (validPapers.length > 0) {
      await Promise.all(
        validPapers.map(async (item: any) => {
          let responseFromFindingEvaluationDetails: any = await findEvaluationDetailsByPaperIdAndDate(item?.id, lookbackDate)
          if (responseFromFindingEvaluationDetails?.evaluations?.length > 0) {
            responseEvaluationDetailsForPaper.push(...responseFromFindingEvaluationDetails.evaluations)
          }
        })
      )
    } else if (relatedPapers.length === 0) {
      return { status: false, message: "Can't add now, there is no related papers" }
    } else {
      return {
        status: false,
        message: "Can't add now, related papers do not have subsection/question structure",
      }
    }

    // Ensure the evaluation being generated is included even if older than the lookback window
    const ensureEvaluationId = options?.ensureEvaluationId
    if (ensureEvaluationId && !responseEvaluationDetailsForPaper.some(evaluation => evaluation?.id === ensureEvaluationId)) {
      const ensured: any = await findEvaluationByIdForNationalAverage(ensureEvaluationId)
      if (ensured?.evaluations?.length > 0) {
        responseEvaluationDetailsForPaper.push(...ensured.evaluations)
      }
    }

    //changed the format, formated the data by each candidate wise
    const newEvaluationDetails: any = await changeFormatCandidateWise(responseEvaluationDetailsForPaper)
    const paperSubsectionDetails: ISubsection[] = []
    if (newEvaluationDetails.length > 0) {
      //total candidates
      for (let candidate = 0; candidate < newEvaluationDetails?.length; candidate++) {
        let candidateDetails = newEvaluationDetails[candidate]
        //finding each candidate result
        const candidataAnswers: any = await findCandidateResultByID(candidateDetails?.id)
        //finding users answers for each questions
        const answers = candidataAnswers?.evaluation_details?.[0]?.answers
        if (!Array.isArray(answers)) {
          continue
        }
        //paper subsections and questions of that subsection
        const paperSubsectionsWithItsIds = candidateDetails?.paper?.subSectionQuestions
        //iterating through all the section
        for (let i = 0; i < paperSubsectionsWithItsIds?.length; i++) {
          //geting all subsection details like section name,section Id etc
          let response: any = await findSubsectionDetailsById(paperSubsectionsWithItsIds[i]?.id)
          let subsectionDetails = response?.paper_template_sub_sections[0]
          const subsectionObject: ISubsection = {
            subsection: subsectionDetails,
            question: [],
          }
          //pushing all subsection
          paperSubsectionDetails.push(subsectionObject)
          //iterating through all the questions in a subsection
          for (let j = 0; j < paperSubsectionsWithItsIds[i].questionIds.length; j++) {
            let questionDetails: any = {}
            //evaluation happening on the questions which are ordered in subsection question in a paper
            questionDetails.candidateResult = answers.shift()
            paperSubsectionDetails[paperSubsectionDetails.length - 1].question?.push(questionDetails)
          }
        }
      }
      //change the formating, for grouping same subsection together
      const sectionBasedFormatedData = await changeformat(paperSubsectionDetails)
      const totalAndSectionalAverages = await findTotalSectionalAverage(sectionBasedFormatedData)
      if (!totalAndSectionalAverages?.status) {
        return {
          status: false,
          message: totalAndSectionalAverages?.message || 'Could not calculate sectional national averages',
        }
      }
      return { status: true, totalAndSectionalAverages }
    } else {
      return {
        status: false,
        message: `No evaluation with candidates found in the last ${lookbackDays} days for this test track/product.`,
      }
    }
  } catch (err) {
    console.log(err, ':error from findNationalAverage')
    return { status: false, message: 'unexpected errors found............' }
  }
}

// Set to 1/1 so a benchmark is shown whenever at least one candidate has taken the subject
// (no blank "-" cells). The value is still the true all-schools population average; raise
// these to suppress thin, single-school benchmarks. Keep in sync with the backend.
export const NATIONAL_AVERAGE_MIN_SAMPLE = 1
export const NATIONAL_AVERAGE_MIN_SCHOOLS = 1

const parseSectionLevelDetails = (raw: any) => {
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch (e) {
    return null
  }
}

// Preferred national-average calculation: aggregate the whole candidate population (all
// schools) from candidate_report_data as the mean of per-candidate percentages — consistent
// with how each school's own average is computed — instead of a rolling window of recent
// evaluations (which biased the value toward the most recently processed school).
export const calculateNationalAverageFromReports = async (testTrackId: string, productId?: string, testType?: string) => {
  try {
    const response: any = await findCandidateReportsForNationalAverage(testTrackId, productId, testType)
    const reports = response?.candidate_report_data
    if (!Array.isArray(reports)) {
      return { status: false, reason: 'lookup_failed' }
    }

    const totalPercentages: number[] = []
    const sectionSums: Record<string, number> = {}
    const sectionCounts: Record<string, number> = {}
    const schools = new Set<string>()

    for (const row of reports) {
      const sld = parseSectionLevelDetails(row?.section_level_details)
      if (!sld) continue
      const totalMax = Number(sld.total_paper_score)
      const totalObtained = Number(sld.total_marks)
      if (!(totalMax > 0)) continue
      totalPercentages.push((totalObtained / totalMax) * 100)
      if (row?.school_id) schools.add(row.school_id)

      const labels = Array.isArray(sld.labelsOfSection) ? sld.labelsOfSection : []
      const obtained = Array.isArray(sld.sectionWiseTotalObtained) ? sld.sectionWiseTotalObtained : []
      const maxes = Array.isArray(sld.sectionWiseTotal) ? sld.sectionWiseTotal : []
      for (let i = 0; i < labels.length; i++) {
        const name = labels[i]
        const secMax = Number(maxes[i])
        const secObtained = Number(obtained[i])
        if (!name || !(secMax > 0)) continue
        sectionSums[name] = (sectionSums[name] || 0) + (secObtained / secMax) * 100
        sectionCounts[name] = (sectionCounts[name] || 0) + 1
      }
    }

    const sampleSize = totalPercentages.length
    const distinctSchools = schools.size
    if (sampleSize === 0) {
      return { status: false, reason: 'no_candidates', sampleSize, distinctSchools }
    }

    const total_national_average = totalPercentages.reduce((a, b) => a + b, 0) / sampleSize
    const section_wise_average = Object.keys(sectionSums).map(name => ({
      name,
      sectional_national_average: sectionSums[name] / sectionCounts[name],
    }))
    const meetsThreshold = sampleSize >= NATIONAL_AVERAGE_MIN_SAMPLE && distinctSchools >= NATIONAL_AVERAGE_MIN_SCHOOLS

    return { status: true, total_national_average, section_wise_average, sampleSize, distinctSchools, meetsThreshold }
  } catch (err) {
    console.log(err, ':error from calculateNationalAverageFromReports')
    return { status: false, reason: 'error' }
  }
}
